import {
  polygon,
  lineString,
  buffer,
  intersect,
  difference,
  booleanWithin,
  lineIntersect,
  kinks,
  getCoords,
} from '@turf/turf';
import areaCalculation from '../../../../algorithms/geometry/AreaCalculation';
import { getLat, getLng } from '../../PolygonDrawingUtils';

interface ValidationResult {
  disable: boolean;
  reason: string;
  fieldASize?: string;
  fieldBSize?: string;
}

interface UpdatedState {
  polygons?: any[];
  actions?: any[];
  acIndex?: number;
}

interface ValidateDrawingResult {
  result: ValidationResult;
  updatedState?: UpdatedState;
}

interface SplitFieldInputs {
  fieldAName: string;
  fieldANumber: string;
  fieldBName: string;
  fieldBNumber: string;
}

interface MergeFieldInputs {
  fieldName: string;
  fieldNumber: string;
}

interface ValidationProps {
  drawingMode: string;
  polygonVisible: boolean;
  polygons: any[];
  markers: any[];
  size: number;
  splitFieldInputs?: SplitFieldInputs;
  mergedFieldInputs?: MergeFieldInputs;
  selectedField?: any;
  LangFile: any;
}

function isDrawnFieldHoleWithinField(polygon: any, field: any) {
  const bufferedField = buffer(field, 0.0001, { units: 'kilometers' });
  return booleanWithin(polygon, bufferedField);
}

function checkDrawnAndExistingHoleOverlap(drawnPolygon: any, selectedField: any) {
  return selectedField.polygon.coordinates.slice(1).some((hole: any) => {
    const holePolygon = polygon([swapCoords(hole)]);
    return intersect(drawnPolygon, holePolygon);
  });
}

function convertCoordsToLatLng(coords: number[][]) {
  return coords.map(([lat, lng]) => new google.maps.LatLng(lat, lng));
}

function swapCoords(coords: [number, number][]) {
  return coords.map(([lat, lng]) => [lng, lat]);
}

function prepareSplitPolygons(clipped: any) {
  const [firstRaw, secondRaw] = clipped.geometry.coordinates.map((poly: any) => poly[0]);
  const first = convertCoordsToLatLng(firstRaw).slice(0, -1);
  const second = convertCoordsToLatLng(secondRaw).slice(0, -1);

  const updatedPolygons = [
    { path: first, valid: true },
    { path: second, valid: true },
  ];

  const updatedActions = [
    [
      {
        polygons: updatedPolygons,
        valid: true,
      },
    ],
  ];

  return { updatedPolygons, updatedActions };
}

export function validateDrawing(props: ValidationProps): ValidateDrawingResult {
  const {
    drawingMode,
    polygonVisible,
    polygons,
    markers,
    size,
    LangFile,
    splitFieldInputs,
    mergedFieldInputs,
    selectedField,
  } = props;

  if (!polygonVisible && drawingMode !== 'SPLIT_FIELD' && drawingMode !== 'SPLIT_FIELD_POLYGONS') {
    return { result: { disable: true, reason: '' } };
  }

  if (!polygons.every((p) => p.valid) && drawingMode !== 'SPLIT_FIELD') {
    return { result: { disable: true, reason: LangFile.PolygonDrawing.invalidPolygon } };
  }

  switch (drawingMode) {
    case 'FIELD_HOLE': {
      const drawnCoords = [
        ...polygons[0].path.map((c) => [getLat(c), getLng(c)]),
        [getLat(polygons[0].path[0]), getLng(polygons[0].path[0])],
      ];
      const drawnPolygon = polygon([drawnCoords]);
      const fieldPolygonCoords = swapCoords(selectedField.polygon.coordinates[0]);
      const fieldPolygon = polygon([fieldPolygonCoords]);

      if (kinks(drawnPolygon).features.length > 0) {
        return {
          result: {
            disable: true,
            reason: '',
          },
        };
      }
      if (!booleanWithin(drawnPolygon, fieldPolygon) && !intersect(drawnPolygon, fieldPolygon)) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.drawnInnerBoundaryOutsideField,
          },
        };
      }
      if (booleanWithin(fieldPolygon, drawnPolygon)) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.drawnInnerBoundaryContainsEntireField,
          },
        };
      }
      if (checkDrawnAndExistingHoleOverlap(drawnPolygon, selectedField)) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.drawnInnerBoundaryOverlapExistingBoundary,
          },
        };
      }

      const intersection = intersect(fieldPolygon, drawnPolygon);
      const converted = intersection
        ? convertCoordsToLatLng(getCoords(intersection)[0])?.slice(0, -1)
        : null;

      if (!isDrawnFieldHoleWithinField(drawnPolygon, fieldPolygon) && converted) {
        if (intersection.geometry.coordinates.length > 1) {
          return {
            result: {
              disable: true,
              reason: LangFile.PolygonDrawing.drawnInnerBoundaryCannotBeFittedToField,
            },
          };
        }
        const updatedPolygons = [{ ...polygons[0], path: converted, fittedToField: true }];
        return {
          result: {
            disable: false,
            reason: LangFile.PolygonDrawing.drawnInnerBoundaryFittedToField,
          },
          updatedState: { polygons: updatedPolygons },
        };
      }

      return {
        result: {
          disable: false,
          reason: '',
        },
      };
    }

    case 'FIELD':
    case 'EDIT_FIELD_BOUNDARIES': {
      if (Number(size) > 5000) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.exceedsLimit,
          },
        };
      }
      return {
        result: {
          disable: false,
          reason: '',
        },
      };
    }

    case 'SPLIT_FIELD': {
      if (markers.length < 2) {
        return {
          result: {
            disable: true,
            reason: '',
          },
        };
      }

      // Create a thin polygon from the drawn line to use for splitting
      const line = lineString(markers.map((m) => [getLat(m.position), getLng(m.position)]));
      const drawnPolygon = buffer(line, 0.09, { units: 'meters' });

      // Check intersections with field boundary
      const fieldPolygon = polygon([swapCoords(selectedField.polygon.coordinates[0])]);
      const intersections = lineIntersect(
        line,
        lineString(swapCoords(selectedField.polygon.coordinates[0]))
      );

      if (intersections.features.length === 0) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.noIntersect,
          },
        };
      }

      if (intersections.features.length > 2) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.intersectMoreThanTwo,
          },
        };
      }

      const clipped = difference(fieldPolygon, drawnPolygon);
      // Clipped result should contain exactly two polygons
      if (!clipped || clipped.geometry.coordinates.length !== 2) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.cannotSplit,
          },
        };
      }

      // Calculate sizes of the resulting polygons before split
      const firstPolygon = {
        coords: clipped.geometry.coordinates[0][0].map(([lat, lng]) => ({
          lng,
          lat,
        })),
      };

      const secondPolygon = {
        coords: clipped.geometry.coordinates[1][0].map(([lat, lng]) => ({
          lng,
          lat,
        })),
      };

      const fieldASize = areaCalculation(firstPolygon).size.toFixed(2);
      const fieldBSize = areaCalculation(secondPolygon).size.toFixed(2);

      return {
        result: {
          disable: false,
          reason: '',
          fieldASize,
          fieldBSize,
        },
      };
    }

    case 'SPLIT_FIELD_POLYGONS': {
      // When drawing mode is 'SPLIT_FIELD_POLYGONS' & polygons are not added to the state, we calculate the split and return the polygons to add them to the state
      if (polygons.length === 0 && markers.length > 1) {
        const line = lineString(markers.map((m) => [getLat(m.position), getLng(m.position)]));
        const drawnPolygon = buffer(line, 0.1, { units: 'meters' });

        const fieldPolygon = polygon([swapCoords(selectedField.polygon.coordinates[0])]);
        const clipped = difference(fieldPolygon, drawnPolygon);

        const { updatedPolygons, updatedActions } = prepareSplitPolygons(clipped);

        return {
          result: {
            disable: false,
            reason: '',
          },
          updatedState: {
            polygons: updatedPolygons,
            actions: updatedActions,
            acIndex: 0,
          },
        };
      }

      // When there are polygons in the state, we validate them
      if (polygons.length === 2) {
        const Acoords = [
          ...polygons[0].path.map((c) => [getLat(c), getLng(c)]),
          [getLat(polygons[0].path[0]), getLng(polygons[0].path[0])],
        ];
        const Bcoords = [
          ...polygons[1].path.map((c) => [getLat(c), getLng(c)]),
          [getLat(polygons[1].path[0]), getLng(polygons[1].path[0])],
        ];

        if (intersect(polygon([Acoords]), polygon([Bcoords]))) {
          return {
            result: {
              disable: true,
              reason: LangFile.PolygonDrawing.splitFieldOverlap,
            },
          };
        }

        if (!splitFieldInputs.fieldANumber?.trim() || !splitFieldInputs.fieldBNumber?.trim()) {
          return {
            result: {
              disable: true,
              reason: LangFile.PolygonDrawing.provideNumbersForSplitFields,
            },
          };
        }
      }

      return {
        result: {
          disable: false,
          reason: '',
        },
      };
    }

    case 'EDIT_MERGED_FIELD': {
      if (Number(size) > 5000) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.exceedsLimit,
          },
        };
      }
      if (!mergedFieldInputs?.fieldNumber?.trim()) {
        return {
          result: {
            disable: true,
            reason: LangFile.PolygonDrawing.provideNumberForMergedField,
          },
        };
      }
      return {
        result: {
          disable: false,
          reason: '',
        },
      };
    }

    default:
      return {
        result: {
          disable: false,
          reason: '',
        },
      };
  }
}
